/**
 * Time Drift Interpolation
 *
 * Binary search-based interpolation for time drift values.
 * O(log n) lookup performance for real-time chart rendering.
 */

import { TimeDriftResult } from '../../domain/lap/TimeDriftCalculator'
import { TimeDriftInterpolation } from './types'

/**
 * Interpolate time drift value for a given distance.
 * Returns only the cumulative time drift (for backward compatibility).
 */
export function interpolateTimeDrift(
  timeDriftResult: TimeDriftResult,
  distance: number
): number {
  const result = interpolateTimeDriftWithRate(timeDriftResult, distance)
  return result.timeDrift
}

/**
 * Interpolate both time drift AND time drift rate for a given distance.
 * This returns both cumulative drift and the rate of change for gradient coloring.
 *
 * Uses binary search for O(log n) lookup performance.
 */
export function interpolateTimeDriftWithRate(
  timeDriftResult: TimeDriftResult,
  distance: number
): TimeDriftInterpolation {
  const driftPoints = timeDriftResult.points

  if (driftPoints.length === 0) {
    return { timeDrift: 0, timeDriftRate: 0 }
  }

  if (distance <= driftPoints[0].distanceMeters) {
    return {
      timeDrift: driftPoints[0].smoothedTimeDeltaMs,
      timeDriftRate: driftPoints[0].timeDriftRate,
    }
  }
  if (distance >= driftPoints[driftPoints.length - 1].distanceMeters) {
    return {
      timeDrift: driftPoints[driftPoints.length - 1].smoothedTimeDeltaMs,
      timeDriftRate: driftPoints[driftPoints.length - 1].timeDriftRate,
    }
  }

  // Binary search for bracketing points
  let low = 0
  let high = driftPoints.length - 1
  while (low < high - 1) {
    const mid = Math.floor((low + high) / 2)
    if (driftPoints[mid].distanceMeters <= distance) {
      low = mid
    } else {
      high = mid
    }
  }

  const lowerPoint = driftPoints[low]
  const upperPoint = driftPoints[high]
  const distanceRange = upperPoint.distanceMeters - lowerPoint.distanceMeters

  if (distanceRange === 0) {
    return {
      timeDrift: lowerPoint.smoothedTimeDeltaMs,
      timeDriftRate: lowerPoint.timeDriftRate,
    }
  }

  const t = (distance - lowerPoint.distanceMeters) / distanceRange
  return {
    timeDrift:
      lowerPoint.smoothedTimeDeltaMs +
      t * (upperPoint.smoothedTimeDeltaMs - lowerPoint.smoothedTimeDeltaMs),
    timeDriftRate:
      lowerPoint.timeDriftRate +
      t * (upperPoint.timeDriftRate - lowerPoint.timeDriftRate),
  }
}
